import apiClient from './client'
import type {
  Organization,
  MonitoredAccount,
  MonitoredAccountRequest,
  BreachEvent,
  BreachCheckResponse,
  BreachsenseCheckResponse,
  AccountLockRequest,
  AccountUnlockRequest,
  PasswordResetRequest,
  LockoutStatusResponse,
  User,
  SummaryStats,
} from './types'

// Organizations
export const getOrganizations = async (): Promise<Organization[]> => {
  const response = await apiClient.get<{ orgs: Organization[]; total: number }>('/orgs/list')
  return response.orgs
}

// Monitored Accounts
export const getMonitoredAccounts = async (orgId: number): Promise<MonitoredAccount[]> => {
  const response = await apiClient.get<{ accounts: MonitoredAccount[]; total_count: number; org_id: number }>(`/monitor/list`, { org_id: orgId, decrypt: true })
  return response.accounts
}

export const addMonitoredAccount = async (
  request: MonitoredAccountRequest
): Promise<MonitoredAccount> => {
  return apiClient.post<MonitoredAccount>('/monitor/add', request)
}

export const removeMonitoredAccount = async (
  email: string,
  orgId: number
): Promise<{ message: string }> => {
  return apiClient.post<{ message: string }>('/monitor/remove', {
    email,
    org_id: orgId,
  })
}

export const scanMonitoredAccount = async (
  email: string,
  orgId: number
): Promise<{
  success: boolean
  email: string
  is_compromised: boolean
  breach_count: number
  risk_score: number
  breaches: string[]
  message: string
}> => {
  return apiClient.post('/monitor/scan', {
    email,
    org_id: orgId,
  })
}

export const getAccountBreaches = async (
  accountId: number,
  orgId: number
): Promise<{
  account_id: number
  email: string
  breach_count: number
  breaches: Array<{
    id: number
    breach_name: string
    source: string
    risk_score: number
    detected_at: string
  }>
}> => {
  return apiClient.get(`/monitor/breaches/${accountId}`, {
    org_id: orgId,
  })
}

// Breach Events
export const getBreachEvents = async (
  orgId: number,
  filters?: {
    source?: 'hibp' | 'breachsense'
    min_risk_score?: number
    max_risk_score?: number
    start_date?: string
    end_date?: string
  }
): Promise<BreachEvent[]> => {
  return apiClient.get<BreachEvent[]>('/breach/events', {
    org_id: orgId,
    ...filters,
  })
}

export const getRecentBreaches = async (
  orgId: number,
  limit: number = 10
): Promise<BreachEvent[]> => {
  return apiClient.get<BreachEvent[]>('/breachsense/recent', {
    org_id: orgId,
    limit,
  })
}

export const checkEmail = async (
  email: string,
  orgId: number
): Promise<BreachCheckResponse> => {
  return apiClient.post<BreachCheckResponse>('/check/email', {
    email,
    org_id: orgId,
  })
}

export const checkBreachsense = async (
  email: string,
  orgId: number
): Promise<BreachsenseCheckResponse> => {
  return apiClient.post<BreachsenseCheckResponse>('/breachsense/check', {
    email,
    org_id: orgId,
  })
}

// Account Lockout
export const lockAccount = async (request: AccountLockRequest): Promise<{ message: string }> => {
  return apiClient.post<{ message: string }>('/account/lock', request)
}

export const unlockAccount = async (
  request: AccountUnlockRequest
): Promise<{ message: string }> => {
  return apiClient.post<{ message: string }>('/account/unlock', request)
}

export const resetPassword = async (
  request: PasswordResetRequest
): Promise<{ message: string }> => {
  return apiClient.post<{ message: string }>('/account/reset-password', request)
}

export const getLockoutStatus = async (
  userId: number,
  orgId: number
): Promise<LockoutStatusResponse> => {
  return apiClient.get<LockoutStatusResponse>('/account/lockout-status', {
    user_id: userId,
    org_id: orgId,
  })
}

// Users (for account lockout page)
export const getUsers = async (orgId: number): Promise<User[]> => {
  const response = await apiClient.get<{ users: User[]; total: number }>('/users/list', {
    org_id: orgId,
  })
  return response.users
}

// Summary Stats (for dashboard)
export const getSummaryStats = async (orgId: number): Promise<SummaryStats> => {
  // Calculate from monitored accounts and breach events
  const [accounts, events] = await Promise.all([
    getMonitoredAccounts(orgId),
    getBreachEvents(orgId),
  ])

  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

  const activeBreaches = events.filter(
    (e) => new Date(e.created_at) >= thirtyDaysAgo
  ).length

  const lockedAccounts = accounts.filter((a) => (a.risk_score || 0) >= 90).length

  const avgRiskScore = accounts.length > 0
    ? Math.round(
        accounts.reduce((sum, a) => sum + (a.risk_score || 0), 0) / accounts.length
      )
    : 0

  return {
    total_monitored: accounts.length,
    active_breaches: activeBreaches,
    locked_accounts: lockedAccounts,
    average_risk_score: avgRiskScore,
  }
}
